import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { getEffectivePrice } from '@3dera/core/utils/pricing';

const CartContext = createContext();

const CART_KEY = '3dera_cart';

const loadCart = () => {
  try {
    const raw = localStorage.getItem(CART_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
};

const saveCart = (items) => {
  localStorage.setItem(CART_KEY, JSON.stringify(items));
};

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used within CartProvider');
  return ctx;
};

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState(loadCart);

  // localStorage'a sync
  useEffect(() => {
    saveCart(items);
  }, [items]);

  const addItem = useCallback((product, seller, qty = 1) => {
    setItems(prev => {
      const existing = prev.find(i => i.productId === product.id);
      if (existing) {
        const maxStock = product.stockCount || 99;
        const newQty = Math.min(existing.quantity + qty, maxStock);
        return prev.map(i =>
          i.productId === product.id ? { ...i, quantity: newQty } : i
        );
      }
      return [...prev, {
        productId: product.id,
        name: product.name,
        price: product.price,
        basePrice: product.price,
        discount: product.discount ?? null,
        fulfillmentType: product.fulfillmentType ?? null,
        poolPrice: product.poolPrice ?? null,
        image: product.images?.[0] || '',
        category: product.category,
        sellerId: product.sellerId,
        sellerName: seller?.publicName || '',
        sellerCity: seller?.location?.city || '',
        sellerImage: seller?.profileImageUrl || '',
        stockCount: product.stockCount || 99,
        quantity: qty,
        addedAt: Date.now()
      }];
    });
  }, []);

  const removeItem = useCallback((productId) => {
    setItems(prev => prev.filter(i => i.productId !== productId));
  }, []);

  const updateQuantity = useCallback((productId, quantity) => {
    if (quantity <= 0) {
      setItems(prev => prev.filter(i => i.productId !== productId));
      return;
    }
    setItems(prev => prev.map(i =>
      i.productId === productId
        ? { ...i, quantity: Math.min(quantity, i.stockCount) }
        : i
    ));
  }, []);

  const clearCart = useCallback(() => {
    setItems([]);
  }, []);

  const isInCart = useCallback((productId) => {
    return items.some(i => i.productId === productId);
  }, [items]);

  // Satıcıya göre grupla
  const groupedBySeller = items.reduce((groups, item) => {
    const key = item.sellerId;
    if (!groups[key]) {
      groups[key] = {
        sellerId: item.sellerId,
        sellerName: item.sellerName,
        sellerCity: item.sellerCity,
        sellerImage: item.sellerImage,
        items: []
      };
    }
    groups[key].items.push(item);
    return groups;
  }, {});

  const itemCount = items.reduce((sum, i) => sum + i.quantity, 0);
  const subtotal = items.reduce((sum, i) => sum + getEffectivePrice(i).price * i.quantity, 0);

  const FREE_SHIPPING_THRESHOLD = 250;
  const SHIPPING_COST = 29.90;
  const isFreeShipping = subtotal >= FREE_SHIPPING_THRESHOLD;
  const shippingFee = isFreeShipping ? 0 : SHIPPING_COST;
  const total = subtotal + shippingFee;

  return (
    <CartContext.Provider value={{
      items,
      itemCount,
      subtotal,
      shippingFee,
      isFreeShipping,
      total,
      FREE_SHIPPING_THRESHOLD,
      SHIPPING_COST,
      groupedBySeller,
      addItem,
      removeItem,
      updateQuantity,
      clearCart,
      isInCart
    }}>
      {children}
    </CartContext.Provider>
  );
};

export default CartContext;
